import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const RequestDemo = () => {
  useEffect(() => {
    // Create and add the HubSpot meetings embed script
    const script = document.createElement('script');
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    script.async = true;
    document.body.appendChild(script);
    
    // Clean up when component unmounts
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Use the shared Header component */}
      <Header />
      
      {/* Main content */}
      <main className="flex-1">
        {/* Hero section */}
        <section className="bg-roundone-dark-purple py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-6 text-white">Book a Demo</h1>
              <p className="text-lg text-roundone-white/80 mb-8">
                Schedule a personalized demo with our team to see how RoundOne AI can transform your candidate screening process.
              </p>
            </div>
          </div>
        </section>
        
        {/* Booking section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-[#E5DEFF]">
                <div className="mb-8 text-center">
                  <h2 className="text-2xl font-bold mb-4">Select a Time That Works For You</h2>
                  <p className="text-muted-foreground">
                    Our product expert will walk you through the platform and answer any questions you have.
                  </p>
                </div>
                
                {/* HubSpot Meetings Embed */}
                <div 
                  className="meetings-iframe-container" 
                  data-src="https://meetings-eu1.hubspot.com/mayur-macwan/demo-scheduler?embed=true"
                  style={{
                    minWidth: '320px',
                    minHeight: '630px',
                    height: '700px',
                    width: '100%'
                  }}
                ></div>
              </div>
              
              {/* Benefits section */}
              <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-[#F8F7FF] p-6 rounded-xl border border-[#E5DEFF]">
                  <div className="w-12 h-12 bg-roundone-purple/10 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                      <path d="M12 8v4l3 3" />
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Save Time</h3>
                  <p className="text-muted-foreground">
                    Learn how RoundOne AI cuts screening time by up to 70% with AI-powered assessments.
                  </p>
                </div>
                
                <div className="bg-[#F8F7FF] p-6 rounded-xl border border-[#E5DEFF]">
                  <div className="w-12 h-12 bg-roundone-purple/10 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Improve Quality</h3>
                  <p className="text-muted-foreground">
                    See how our platform helps you identify the best candidates with greater accuracy.
                  </p>
                </div>
                
                <div className="bg-[#F8F7FF] p-6 rounded-xl border border-[#E5DEFF]">
                  <div className="w-12 h-12 bg-roundone-purple/10 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#9b87f5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Personalized Demo</h3>
                  <p className="text-muted-foreground">
                    Get answers to your specific questions and see how RoundOne fits your hiring process.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Reminder CTA Section */}
        <section className="py-12 bg-[#F8F7FF] border-t border-[#E5DEFF]">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8 border border-[#E5DEFF]">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-2xl font-bold mb-2">Don't want to wait?</h3>
                  <p className="text-muted-foreground">
                    Start screening candidates today with our free plan. No credit card required.
                  </p>
                </div>
                <Link to="/sign-in?tab=register">
                  <Button 
                    className="gradient-button py-6 px-8 text-lg whitespace-nowrap rounded-full transition-all duration-300 hover:shadow-lg" 
                  >
                    Get started for free
                    <ArrowRight className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      {/* Use the shared Footer component */}
      <Footer />
    </div>
  );
};

export default RequestDemo; 